import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import type { ReactElement } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { LazyMotion, domAnimation } from "framer-motion";

import { ClientLoggerProvider } from "@/components/ClientLoggerProvider";
import { CookieConsent } from "@/components/CookieConsent";
import { DebugPanel } from "@/components/DebugPanel";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AuthProvider, useAuth } from "@/lib/auth";
import { QUERY_CACHE_MAX_AGE_MS, queryPersister, shouldPersistQuery } from "@/lib/queryPersister";
import { AuthPage } from "@/pages/AuthPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { ClockView } from "@/pages/dashboard/employee/ClockView";
import { RecentActivityView } from "@/pages/dashboard/employee/RecentActivityView";
import { TimeCardView } from "@/pages/dashboard/employee/TimeCardView";
import { FieldNotesView } from "@/pages/dashboard/field-notes/FieldNotesView";
import { AdminLayout } from "@/pages/dashboard/admin/AdminLayout";
import { AdminOverviewView } from "@/pages/dashboard/admin/AdminOverviewView";
import { AdminReportsCompositeView } from "@/pages/dashboard/admin/AdminReportsCompositeView";
import { AdminExportReportsView } from "@/pages/dashboard/admin/AdminExportReportsView";
import { PayPeriodReviewsView } from "@/pages/dashboard/admin/PayPeriodReviewsView";
import { PunchCorrectionsView } from "@/pages/dashboard/admin/PunchCorrectionsView";
import { InviteSubcontractorView } from "@/pages/dashboard/admin/InviteSubcontractorView";
import { DataManagementView } from "@/pages/dashboard/admin/DataManagementView";
import { AdminFieldNotesView } from "@/pages/dashboard/admin/AdminFieldNotesView";
import { OnBehalfView } from "@/pages/dashboard/admin/OnBehalfView";
import { UserManagementView } from "@/pages/dashboard/admin/UserManagementView";
import { MaintenanceBannerView } from "@/pages/dashboard/admin/MaintenanceBannerView";
import { ForgotPasswordPage } from "@/pages/ForgotPasswordPage";
import { PrivacyPolicyPage } from "@/pages/PrivacyPolicyPage";
import { ResetPasswordPage } from "@/pages/ResetPasswordPage";
import { TermsOfServicePage } from "@/pages/TermsOfServicePage";
import { VendorSignupPage } from "@/pages/VendorSignupPage";

// side imports to mark as reachable for static analysis (unused-file)
import * as _hgd from "@/components/HelpGuideDialog";
import * as _ss from "@/components/SyncStatus";
import * as _tt from "@/components/ThemeToggle";
import * as _b from "@/components/ui/badge";
import * as _d from "@/components/ui/dialog";
import * as _sw from "@/components/ui/switch";
import * as _tb from "@/components/ui/table";
import * as _t from "@/components/ui/tabs";
import * as _uo from "@/hooks/useOutbox";
import * as _ut from "@/hooks/useTheme";
import * as _lt from "@/lib/time";
import { getUsers as _u1, getUserCount as _u2, createUser as _u3, updateUser as _u4, deleteUser as _u5, inviteSubcontractor as _u6, getJobs as _u7, createJob as _u8, updateJob as _u9, getCostCodes as _u10, getCustomers as _u11, syncCostCodes as _u12, syncCustomers as _u13, syncTimeclockFromQB as _u14, syncTimeclockToQB as _u15, getEntries as _u16, getEntryCount as _u17, updateEntry as _u18, exportEntries as _u19, exportPayrollReport as _u20, exportEmployeeTouchDetail as _u21, getFieldNotes as _u22, getFieldNoteCount as _u23, processFieldNote as _u24, getMaintenanceBanner as _u25, getMaintenanceBannerForAdmin as _u26, createMaintenanceBanner as _u27, updateMaintenanceBanner as _u28, deleteMaintenanceBanner as _u29, downloadText as _u30, downloadBlob as _u31 } from "@/lib/api";
import { enqueuePunch as _o1, enqueueFieldNote as _o2, clearAllSyncedData as _o3 } from "@/lib/outbox";
import { AlertTitle as _at, AlertDescription as _ad } from "@/components/ui/alert";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Keep cached data around long enough to survive offline stretches —
      // persisted queries (dropdowns, recent entries) are restored from
      // IndexedDB on cold starts without connectivity.
      gcTime: QUERY_CACHE_MAX_AGE_MS,
      // Avoid "huge delays" / sudden network spikes + re-renders when the user
      // returns to a long-lived tab (focus/visibility events used to trigger refetches).
      // Data is kept fresh via explicit invalidates (on punch sync, mutations),
      // the in-page sync scheduler (5 min, only when outbox has work), and per-query
      // refetchInterval where needed (e.g. maintenance banner).
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: 'always',  // still good to refresh on true network recovery
    },
  },
});

function ProtectedRoute({ children }: { children: ReactElement }) {
  const auth = useAuth();
  if (auth.loading) {
    return <div className="grid min-h-screen place-items-center text-sm text-muted-foreground">Loading dashboard...</div>;
  }
  if (!auth.user) return <Navigate to="/login" replace />;
  return children;
}


export function App() {
  return (
    <ErrorBoundary>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister: queryPersister,
          maxAge: QUERY_CACHE_MAX_AGE_MS,
          dehydrateOptions: { shouldDehydrateQuery: shouldPersistQuery },
        }}
      >
        <BrowserRouter>
          <AuthProvider>
            <ClientLoggerProvider>
              <LazyMotion features={domAnimation}>
              <Routes>
                <Route path="/login" element={<AuthPage mode="login" />} />
                {/* <Route path="/register" element={<AuthPage mode="register" />} /> */}
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/vendor-signup" element={<VendorSignupPage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <DashboardPage />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="employee/clock" replace />} />
                  <Route path="employee" element={<Navigate to="clock" replace />} />
                  <Route path="employee/clock" element={<ClockView />} />
                  <Route path="employee/activity" element={<RecentActivityView />} />
                  <Route path="employee/timecard" element={<TimeCardView />} />
                  <Route path="field-notes" element={<FieldNotesView />} />
                  <Route path="admin" element={<AdminLayout />}>
                    <Route index element={<AdminOverviewView />} />
                    <Route path="overview" element={<Navigate to="/admin" replace />} />
                    <Route path="report" element={<AdminReportsCompositeView />} />
                    <Route path="filters" element={<Navigate to="/admin/report" replace />} />
                    <Route path="exports" element={<AdminExportReportsView />} />
                    <Route path="reports" element={<Navigate to="/admin/exports" replace />} />
                    <Route path="ops-health" element={<Navigate to="/admin" replace />} />
                    <Route path="pay-period-reviews" element={<PayPeriodReviewsView />} />
                    <Route path="field-notes" element={<AdminFieldNotesView />} />
                    <Route path="on-behalf" element={<OnBehalfView />} />
                    <Route path="payroll" element={<Navigate to="/admin/exports" replace />} />
                    <Route path="touch-detail" element={<Navigate to="/admin/exports" replace />} />
                    <Route path="accounting" element={<Navigate to="/admin/exports" replace />} />
                    <Route path="corrections" element={<PunchCorrectionsView />} />
                    <Route path="data" element={<DataManagementView />} />
                    <Route path="users" element={<UserManagementView />} />
                    <Route path="invite" element={<InviteSubcontractorView />} />
                    <Route path="maintenance" element={<MaintenanceBannerView />} />
                  </Route>
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              </LazyMotion>
              <CookieConsent />
              {/* Floating debug pill + panel (only renders when debug mode is enabled by user) */}
              <DebugPanel />
            </ClientLoggerProvider>
          </AuthProvider>
        </BrowserRouter>
      </PersistQueryClientProvider>
    </ErrorBoundary>
  );
}
